import { getAuthHeaders, isMockAuthActive } from './authHeaders';
import { handleAuthSessionExpired, refreshAccessToken } from './tokenRefresh';

function mergeHeaders(init?: RequestInit): Record<string, string> {
  const authHeaders = getAuthHeaders();
  if (!init?.headers) {
    return authHeaders;
  }

  if (init.headers instanceof Headers) {
    const merged = { ...authHeaders };
    init.headers.forEach((value, key) => {
      merged[key] = value;
    });
    return merged;
  }

  if (Array.isArray(init.headers)) {
    const merged = { ...authHeaders };
    for (const [key, value] of init.headers) {
      merged[key] = value;
    }
    return merged;
  }

  return { ...authHeaders, ...init.headers };
}

/**
 * Per-request timeout. Long enough for slow DB queries, short enough that a
 * stalled connection doesn't hang the UI until the browser/test default.
 */
const DEFAULT_TIMEOUT_MS = 15_000;
/**
 * Max retries on transient failures. 1 initial attempt + 2 retries = 3 total.
 * Kept low to avoid amplifying load during outages.
 */
const DEFAULT_NETWORK_RETRIES = 2;
const BASE_BACKOFF_MS = 400;

const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

export interface FetchWithAuthOptions {
  /** Per-request timeout in ms (default 15000). */
  timeoutMs?: number;
  /** Max retries on transient network errors / 5xx (default 2; 0 disables). */
  networkRetries?: number;
}

/**
 * Classify an error from `fetch()` as retryable.
 * - `TypeError` ("Failed to fetch"): network/CORS/DNS failure — retry.
 * - Our own timeout (controller abort flagged via `timedOut`): retry.
 * - `AbortError` from a caller-supplied signal: NOT retryable (intentional).
 */
function isRetryableError(err: unknown, timedOut: boolean): boolean {
  if (timedOut) return true;
  return err instanceof TypeError; // network error: "Failed to fetch"
}

function backoffDelay(attempt: number): number {
  // Exponential backoff with small jitter: 400ms, 800ms, ...
  const base = BASE_BACKOFF_MS * Math.pow(2, attempt - 1);
  return base + Math.floor(Math.random() * 120);
}

/**
 * Authenticated fetch with:
 *  - silent OIDC token refresh on 401 (concurrent 401s share one in-flight
 *    refresh via refreshAccessToken(); mock-auth mode skips refresh).
 *  - per-request timeout (default 15s)
 *  - bounded retry with exponential backoff for transient network errors
 *    (all methods) and 5xx responses (idempotent methods only). 4xx is never
 *    retried — those are deterministic client errors.
 *
 * Callers can tune or disable resilience via the third argument, e.g.
 * `fetchWithAuth(url, { method: 'POST' }, { networkRetries: 0 })`.
 */
export async function fetchWithAuth(
  input: RequestInfo | URL,
  init?: RequestInit,
  options: FetchWithAuthOptions = {}
): Promise<Response> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const maxRetries = options.networkRetries ?? DEFAULT_NETWORK_RETRIES;
  const method = (init?.method ?? 'GET').toUpperCase();
  const idempotent = IDEMPOTENT_METHODS.has(method);
  const callerSignal = init?.signal;

  let refreshedThisCall = false;
  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    // Compose a per-attempt timeout signal with any caller-supplied signal.
    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
    if (callerSignal) {
      if (callerSignal.aborted) {
        controller.abort();
      } else {
        callerSignal.addEventListener('abort', () => controller.abort(), { once: true });
      }
    }

    try {
      const headers = mergeHeaders(init);
      const response = await fetch(input, { ...init, headers, signal: controller.signal });

      // 401 → silent token refresh + retry once (real auth only).
      if (response.status === 401 && !isMockAuthActive() && !refreshedThisCall) {
        refreshedThisCall = true;
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          handleAuthSessionExpired();
          return response;
        }
        // Loop again; mergeHeaders() will pick up the refreshed token.
        continue;
      }

      // 5xx on idempotent methods → retry with backoff (unless last attempt).
      if (response.status >= 500 && idempotent && attempt < maxRetries) {
        await new Promise((r) => setTimeout(r, backoffDelay(attempt + 1)));
        continue;
      }

      return response;
    } catch (err) {
      lastError = err;
      // Caller-initiated abort or non-retryable error: propagate immediately.
      if (!isRetryableError(err, timedOut) || attempt >= maxRetries) {
        throw err;
      }
      // Transient network error / our timeout → backoff and retry.
      await new Promise((r) => setTimeout(r, backoffDelay(attempt + 1)));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // Unreachable: the loop either returns a Response or throws on the last attempt.
  throw lastError;
}
